import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useXP } from '../contexts/XPContext';
import { useToast } from '../components/ui/Toast';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function MockInterviewResults() {
  const { interviewId } = useParams();
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const { notifyXPGain } = useXP();
  const { showError } = useToast();

  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generatingFeedback, setGeneratingFeedback] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const xpNotifiedRef = useRef(false); // Track if we've already notified XP for this interview

  const MAX_RETRIES = 18; // 18 retries * 10 seconds = 3 minutes max wait
  const RETRY_INTERVAL = 10000; // 10 seconds between retries

  useEffect(() => {
    loadResults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Polling effect for when feedback is being generated
  useEffect(() => {
    let pollTimer;

    if (generatingFeedback && retryCount < MAX_RETRIES) {
      pollTimer = setTimeout(() => {
        loadResults(true); // true = is a retry
      }, RETRY_INTERVAL);
    }

    return () => {
      if (pollTimer) clearTimeout(pollTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generatingFeedback, retryCount]);

  const loadResults = async (isRetry = false) => {
    const token = getToken();
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/api/mock-interview/${interviewId}/results`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load results');
      }

      const data = await response.json();

      // Check if feedback is still being generated (any non-completed status)
      // Also handle case where status is missing (shouldn't happen but be safe)
      const isStillGenerating =
        data.status === 'generating_feedback' ||
        data.status === 'closing' ||
        data.status === 'in_progress' ||
        !data.status;  // Missing status means something is wrong

      if (isStillGenerating) {
        setGeneratingFeedback(true);
        if (isRetry) {
          setRetryCount(prev => prev + 1);
        }
        // Don't set results yet - keep showing the generating state
        setLoading(false);
        return;
      }

      // Check if we got 'completed' status but data is actually empty/missing
      // Use == null to catch both undefined and null
      const hasEmptyData =
        data.overall_score == null ||
        data.overall_score === 0 ||
        !data.feedback ||
        !data.feedback.question_scores ||
        data.feedback.question_scores.length === 0;

      if (hasEmptyData && retryCount < MAX_RETRIES) {
        setGeneratingFeedback(true);
        if (isRetry) {
          setRetryCount(prev => prev + 1);
        }
        setLoading(false);
        return;
      }

      // We have valid results (or exhausted retries)
      setGeneratingFeedback(false);
      setResults(data);

      // Notify XP gain if XP was awarded (only once per interview)
      if (data.feedback?.xp_awarded && !xpNotifiedRef.current) {
        xpNotifiedRef.current = true;
        notifyXPGain(data.feedback.xp_awarded);
      }
    } catch (error) {
      if (!isRetry) {
        showError('Failed to load interview results');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg-base)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-primary)' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '3px solid var(--border-subtle)',
            borderTop: '3px solid var(--indigo-500)',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          <p style={{ color: 'var(--text-tertiary)' }}>Loading results...</p>
        </div>
      </div>
    );
  }

  // Show generating feedback UI
  if (generatingFeedback || (!results && retryCount < MAX_RETRIES)) {
    const progressPercent = Math.min((retryCount / MAX_RETRIES) * 100, 95);
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg-base)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-primary)' }}>
        <div style={{ textAlign: 'center', maxWidth: '400px', padding: '40px' }}>
          <div style={{ marginBottom: '24px' }}>
            <style>{`
              @keyframes pulse {
                0%, 100% { opacity: 1; transform: scale(1); }
                50% { opacity: 0.7; transform: scale(1.1); }
              }
              @keyframes spin {
                from { transform: rotate(0deg); }
                to { transform: rotate(360deg); }
              }
            `}</style>
            <div style={{
              width: '64px',
              height: '64px',
              border: '4px solid var(--border-subtle)',
              borderTop: '4px solid var(--indigo-500)',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto'
            }} />
          </div>
          <h2 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '12px', color: 'var(--indigo-500)' }}>
            Generating Your Feedback
          </h2>
          <p style={{ color: 'var(--text-tertiary)', fontSize: '16px', marginBottom: '24px', lineHeight: '1.6' }}>
            Our AI interviewer is carefully analyzing your responses and preparing detailed, actionable feedback. This usually takes 2-3 minutes.
          </p>

          {/* Progress bar */}
          <div style={{
            width: '100%',
            height: '8px',
            background: 'var(--bg-elevated)',
            borderRadius: '4px',
            overflow: 'hidden',
            marginBottom: '16px'
          }}>
            <div style={{
              width: `${progressPercent}%`,
              height: '100%',
              background: 'var(--gradient-primary)',
              transition: 'width 0.5s ease'
            }} />
          </div>

          <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>
            Checking for results... ({retryCount + 1}/{MAX_RETRIES})
          </p>
        </div>
      </div>
    );
  }

  if (!results) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg-base)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-primary)' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '48px',
            height: '48px',
            background: 'rgba(239, 68, 68, 0.2)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px'
          }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="15" y1="9" x2="9" y2="15"/>
              <line x1="9" y1="9" x2="15" y2="15"/>
            </svg>
          </div>
          <p style={{ color: 'var(--text-tertiary)' }}>Results not found</p>
          <button
            onClick={() => navigate('/interview-prep')}
            style={{
              marginTop: '20px',
              padding: '12px 24px',
              background: 'var(--gradient-primary)',
              color: 'var(--text-primary)',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600'
            }}
          >
            Back to Interview Prep
          </button>
        </div>
      </div>
    );
  }

  // Extract results with defaults
  const overall_score = results.overall_score || 0;
  const hiring_decision = results.hiring_decision || 'maybe';
  const hiring_reason = results.hiring_reason || 'Feedback not available';
  const feedback = results.feedback || {
    critical_assessment: [],
    strengths: [],
    weaknesses: [],
    question_scores: [],
    recommended_actions: [],
    interviewer_notes: ''
  };

  // Decision banner styling
  const decisionStyles = {
    'hire': {
      background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
      text: 'WOULD HIRE'
    },
    'maybe': {
      background: 'linear-gradient(135deg, #d97706 0%, #f59e0b 100%)',
      text: 'MAYBE / NEEDS DISCUSSION'
    },
    'no_hire': {
      background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
      text: 'WOULD NOT ADVANCE'
    }
  };

  const decisionStyle = decisionStyles[hiring_decision] || decisionStyles['maybe'];

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-base)', color: 'var(--text-primary)', padding: '40px 20px' }}>
      <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '32px' }}>
          <button
            onClick={() => navigate('/interview-prep')}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--text-tertiary)',
              fontSize: '14px',
              cursor: 'pointer',
              marginBottom: '20px'
            }}
          >
            ← Back to Interview Prep
          </button>
          <h1 style={{ fontSize: '36px', fontWeight: '700', marginBottom: '8px' }}>
            Interview Complete
          </h1>
          <p style={{ color: 'var(--text-tertiary)', fontSize: '16px' }}>
            {results.company_name} - {results.job_role}
          </p>
        </div>

        {/* Hiring Decision Banner */}
        <div style={{
          ...{ padding: '24px', borderRadius: '16px', textAlign: 'center', fontSize: '28px', fontWeight: '700', marginBottom: '32px' },
          background: decisionStyle.background
        }}>
          {decisionStyle.text}
        </div>

        {/* Overall Score */}
        <div style={{
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-light)',
          borderRadius: '16px',
          padding: '32px',
          marginBottom: '24px',
          textAlign: 'center'
        }}>
          <h2 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '16px' }}>Overall Score</h2>
          <div style={{ fontSize: '64px', fontWeight: '700', color: 'var(--indigo-500)', marginBottom: '12px' }}>
            {overall_score}/100
          </div>
          <div style={{
            width: '100%',
            height: '12px',
            background: 'var(--bg-elevated)',
            borderRadius: '6px',
            overflow: 'hidden',
            margin: '20px 0'
          }}>
            <div style={{
              width: `${overall_score}%`,
              height: '100%',
              background: 'var(--gradient-primary)',
              transition: 'width 1s ease'
            }} />
          </div>
        </div>

        {/* Hiring Reason */}
        <div style={{
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-light)',
          borderRadius: '16px',
          padding: '24px',
          marginBottom: '24px'
        }}>
          <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '12px', color: 'var(--indigo-500)' }}>
            Why This Decision:
          </h3>
          <p style={{ fontSize: '16px', lineHeight: '1.6', color: 'var(--text-secondary)' }}>
            {hiring_reason}
          </p>
        </div>

        {/* Critical Assessment */}
        {feedback.critical_assessment && feedback.critical_assessment.length > 0 && (
          <div style={{
            background: 'rgba(220, 38, 38, 0.1)',
            border: '2px solid #dc2626',
            borderRadius: '16px',
            padding: '24px',
            marginBottom: '24px'
          }}>
            <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '16px', color: '#ef4444' }}>
              Critical Issues:
            </h3>
            <ul style={{ marginLeft: '20px', color: '#fca5a5', lineHeight: '1.8' }}>
              {feedback.critical_assessment.map((issue, i) => (
                <li key={i} style={{ fontSize: '15px', marginBottom: '8px' }}>{issue}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Strengths & Weaknesses Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
          {/* Strengths */}
          <div style={{
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-light)',
            borderRadius: '16px',
            padding: '24px'
          }}>
            <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '16px', color: '#22c55e' }}>
              Strengths
            </h3>
            {feedback.strengths && feedback.strengths.length > 0 ? (
              <ul style={{ marginLeft: '20px', color: 'var(--text-secondary)', lineHeight: '1.8' }}>
                {feedback.strengths.map((s, i) => (
                  <li key={i} style={{ fontSize: '14px', marginBottom: '8px' }}>{s}</li>
                ))}
              </ul>
            ) : (
              <p style={{ color: 'var(--text-tertiary)', fontSize: '14px' }}>No notable strengths identified</p>
            )}
          </div>

          {/* Weaknesses */}
          <div style={{
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-light)',
            borderRadius: '16px',
            padding: '24px'
          }}>
            <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '16px', color: '#fbbf24' }}>
              Areas to Improve
            </h3>
            {feedback.weaknesses && feedback.weaknesses.length > 0 ? (
              <ul style={{ marginLeft: '20px', color: 'var(--text-secondary)', lineHeight: '1.8' }}>
                {feedback.weaknesses.map((w, i) => (
                  <li key={i} style={{ fontSize: '14px', marginBottom: '8px' }}>{w}</li>
                ))}
              </ul>
            ) : (
              <p style={{ color: 'var(--text-tertiary)', fontSize: '14px' }}>No major weaknesses identified</p>
            )}
          </div>
        </div>

        {/* Question-by-Question Breakdown */}
        {feedback.question_scores && feedback.question_scores.length > 0 && (
          <div style={{
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-light)',
            borderRadius: '16px',
            padding: '24px',
            marginBottom: '24px'
          }}>
            <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '16px' }}>
              Question-by-Question Scores
            </h3>
            {feedback.question_scores.map((q, i) => (
              <details key={i} style={{
                background: 'var(--bg-surface)',
                border: '1px solid var(--border-light)',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '12px',
                cursor: 'pointer'
              }}>
                <summary style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  fontWeight: '600',
                  fontSize: '15px'
                }}>
                  <span>Q{q.question_number}: {q.question_type}</span>
                  <span style={{
                    padding: '4px 12px',
                    background: q.score >= 7 ? 'rgba(34, 197, 94, 0.2)' : q.score >= 5 ? 'rgba(251, 191, 36, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                    color: q.score >= 7 ? '#22c55e' : q.score >= 5 ? '#fbbf24' : '#ef4444',
                    borderRadius: '6px',
                    fontWeight: '700'
                  }}>
                    {q.score}/10
                  </span>
                </summary>
                <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--border-light)' }}>
                  <p style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: '1.6', marginBottom: '12px' }}>
                    {q.feedback}
                  </p>
                  {q.critical_issues && q.critical_issues.length > 0 && (
                    <div style={{ marginTop: '12px' }}>
                      <div style={{ fontSize: '13px', fontWeight: '600', color: '#ef4444', marginBottom: '8px' }}>
                        Issues:
                      </div>
                      <ul style={{ marginLeft: '20px', color: '#fca5a5' }}>
                        {q.critical_issues.map((issue, j) => (
                          <li key={j} style={{ fontSize: '13px', marginBottom: '4px' }}>• {issue}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </details>
            ))}
          </div>
        )}

        {/* Recommended Actions */}
        {feedback.recommended_actions && feedback.recommended_actions.length > 0 && (
          <div style={{
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-light)',
            borderRadius: '16px',
            padding: '24px',
            marginBottom: '24px'
          }}>
            <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '16px', color: 'var(--indigo-500)' }}>
              What to Do Next:
            </h3>
            <ul style={{ marginLeft: '20px', color: 'var(--text-secondary)', lineHeight: '1.8' }}>
              {feedback.recommended_actions.map((action, i) => (
                <li key={i} style={{ fontSize: '15px', marginBottom: '10px' }}>{action}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Interviewer Notes */}
        {feedback.interviewer_notes && (
          <div style={{
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-light)',
            borderRadius: '16px',
            padding: '24px',
            marginBottom: '32px'
          }}>
            <h4 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '12px', color: 'var(--text-tertiary)' }}>
              Interviewer's Notes:
            </h4>
            <p style={{ fontSize: '14px', fontStyle: 'italic', color: 'var(--text-secondary)', lineHeight: '1.6' }}>
              "{feedback.interviewer_notes}"
            </p>
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: '16px', justifyContent: 'center' }}>
          <button
            onClick={() => navigate('/interview-prep')}
            style={{
              padding: '14px 32px',
              background: 'var(--gradient-primary)',
              color: 'var(--text-primary)',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '600'
            }}
          >
            Practice Again
          </button>
          <button
            onClick={() => navigate('/mock-interview/history')}
            style={{
              padding: '14px 32px',
              background: 'var(--border-light)',
              color: 'var(--text-primary)',
              border: '1px solid var(--border-subtle)',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '600'
            }}
          >
            View History
          </button>
        </div>
      </div>
    </div>
  );
}

export default MockInterviewResults;
